import React, { useState, useEffect } from 'react';
import { Link, useHistory, useLocation } from 'react-router-dom';
import Helmet from 'react-helmet';
import { useSelector } from 'react-redux';
import { Button } from '@material-ui/core';

import { Privileges } from 'utils/privileges';
import ChallengesPageBanner from 'assets/pageBanners/challenges.avif';
import { selectUser, selectOrganization } from 'entities/user/selectors';
import { PageBannerContainer } from 'components/PageBannerContainer/PageBannerContainer';
import { useChallengesQuery, ChallengesQueryVariables, SortOrder } from 'generated/graphql';

import ChallengeList from './ChallengeList';
import { FilterStatus } from './Filters';

const ChallengeCreationButton = () => {
  const toSlug = '/challenges/new';

  return (
    <Link to={toSlug}>
      <Button color="primary" variant="outlined" size="large">
        Create Challenge
      </Button>
    </Link>
  );
};

const DEFAULT_TAKE = 10;
const defaultSortOrder = SortOrder.Asc;
const defaultSortColumn = 'startDate';

const defaultQueryVariables: ChallengesQueryVariables = {
  take: DEFAULT_TAKE,
  skip: 0,
  orderByList: [
    {
      startDate: defaultSortOrder,
    },
  ],
};

const ChallengesPage = () => {
  const history = useHistory();
  const location = useLocation();
  const user = useSelector(selectUser);
  const organization = useSelector(selectOrganization);
  const [queryVariables, setQueryVariables] =
    useState<ChallengesQueryVariables>(defaultQueryVariables);
  const [searchValue, setSearchValue] = useState('');
  const [selectedFilterStatus, setSelectedFilterStatus] = useState<FilterStatus>('All');
  const searchParams = new URLSearchParams(location.search);
  const isChallengesEnabled =
    user && user?.hasPrivilege(Privileges.CHALLENGES_VIEW) && organization?.isCompany();

  const {
    data,
    error: challengesError,
    loading: challengesLoading,
  } = useChallengesQuery({
    variables: queryVariables,
    fetchPolicy: 'network-only',
    skip: !isChallengesEnabled,
  });

  if (!isChallengesEnabled) {
    history.replace('/');
  }

  const handleSortChange = (column: string): void => {
    const previousOrder = searchParams.get('order') || defaultSortOrder;
    const order = previousOrder === 'asc' ? 'desc' : 'asc';

    searchParams.set('column', column);
    searchParams.set('order', order);

    history.replace({
      pathname: location.pathname,
      search: `?${searchParams.toString()}`,
    });
  };

  useEffect(() => {
    const order = searchParams.get('order');
    const column = searchParams.get('column');

    if (!queryVariables || !order || !column) {
      return;
    }

    const newQueryVariables = {
      ...queryVariables,
      orderByList: [
        {
          [column]: order,
        },
      ],
      skip: 0,
    };

    if (
      JSON.stringify(newQueryVariables?.orderByList) === JSON.stringify(queryVariables?.orderByList)
    ) {
      return;
    }

    setQueryVariables(newQueryVariables as ChallengesQueryVariables);
  }, [searchParams, queryVariables, setQueryVariables]);

  const currentSortColumn = searchParams.get('column') || defaultSortColumn;
  const currentSortOrder = (searchParams.get('order') || defaultSortOrder) as 'asc' | 'desc';

  return (
    <>
      <Helmet title="Challenges" />
      <PageBannerContainer color="#DEFFCA" backgroundImage={ChallengesPageBanner}>
        <h1>Challenges</h1>
        <ChallengeCreationButton />
      </PageBannerContainer>

      <ChallengeList
        data={data}
        loading={challengesLoading}
        error={challengesError}
        queryVariables={queryVariables}
        setQueryVariables={(newQueryVariables) => {
          if (JSON.stringify(newQueryVariables) === JSON.stringify(queryVariables)) {
            return;
          }
          setQueryVariables(newQueryVariables);
        }}
        searchValue={searchValue}
        setSearchValue={setSearchValue}
        selectedFilterStatus={selectedFilterStatus}
        setSelectedFilterStatus={setSelectedFilterStatus}
        onSortChange={handleSortChange}
        currentSortColumn={currentSortColumn}
        currentSortOrder={currentSortOrder}
      />
    </>
  );
};

export default ChallengesPage;
